import React, {useRef} from 'react';
import {Row, Col} from 'react-bootstrap';

import '../Playlist/Playlist.css';

import VideoJS from '../Videos/VideoJS.js';
import {getFormatUTCTime} from '../../../../helpers/timestamps';

import ScheduledPlaylistVideo from './ScheduledPlaylistVideo.js';
import {useScheduledPlaylist} from './useScheduledPlaylist.js';

export default function ScheduledPlaylist({playlistData}) {
  const {playlist, goToNext, onSeek, currentTime, infoRef, toggleInfo} =
    useScheduledPlaylist(playlistData);
  const playerRef = useRef(null);

  const videoJsOptions = {
    autoplay: true,
    controls: true,
    responsive: true,
    fill: true,
    poster: playlist.video.url
      ? playlist.video.thumbnail
      : playlist.videos[0]?.thumbnail,
    sources: [
      {
        src: playlist.video.url ? playlist.video.url : '',
        type: playlist.video.url_type ? playlist.video.url_type : ''
      }
    ]
  };

  const handlePlayerReady = (player) => {
    playerRef.current = player;
    player.on('ended', goToNext);
    player.on('seeking', onSeek);
    player.on('seeked', onSeek);
    player.on('play', toggleInfo);
    player.on('pause', toggleInfo);
  };

  const clearPlayerReady = (player) => {
    player.off('ended', goToNext);
    player.off('seeking', onSeek);
    player.off('seeked', onSeek);
    player.off('play', toggleInfo);
    player.off('pause', toggleInfo);
  };

  const videosJsx = playlist.videos.map((video, index) => {
    return (
      <ScheduledPlaylistVideo
        key={video.id}
        video={video}
        upNext={index == 0}
      />
    );
  });

  if (playlist.scheduled.is_complete) {
    return (
      <Col className="mb-5">
        <h3>Playlist Complete. Come back tomorrow.</h3>
      </Col>
    );
  }

  return (
    <Col className="mb-5">
      <div className="video-area">
        <Row className="video-area-background row d-flex">
          <Col lg={8} className="video-area-left mb-3 m-lg-0 p-0 pr-lg-3">
            <div className="video-player-area stream position-relative">
              {Object.keys(playlist.video).length > 0 ? (
                <VideoJS
                  options={videoJsOptions}
                  onReady={handlePlayerReady}
                  onClearReady={clearPlayerReady}
                  onComplete={goToNext}
                  currentTime={currentTime}
                />
              ) : (
                <div className="stream_video">
                  <div className="stream_video_wrapper">
                    <div
                      className="d-flex flex-grow-1 video-thumbnail-placeholder"
                      style={{backgroundImage: `url(${videoJsOptions.poster})`}}
                    ></div>
                  </div>
                </div>
              )}

              {playlist.video.source?.name && (
                <a
                  id="source_area"
                  className="video-source-area p-2"
                  href={playlist.video.source.link}
                  rel="noreferrer"
                  target="_blank"
                >
                  <svg
                    viewBox="0 0 32 32"
                    className="svg-icon svg-icon-12x12 icon-ic_open_in_new svg-icon-source-link svg-icon-white mr-2"
                  >
                    <use xlinkHref="#icon-ic_open_in_new"></use>
                  </svg>
                  <span id="source_name" className="source_link">
                    {playlist.video.source.name}
                  </span>
                </a>
              )}

              <div
                className={
                  'video-details-area p-3 w-100' +
                  (playlist.scheduled.is_playing
                    ? ' video-details-area-above-bar'
                    : '')
                }
                ref={infoRef}
              >
                <div className="video-details mb-1">
                  {playlist.video?.title ? (
                    <React.Fragment>
                      <h5 className="video-title ellipsis m-0 font-weight-bold">
                        {playlist.video.title}
                      </h5>
                      <h5 className="video-description m-0 d-none">
                        {playlist.video.description}
                      </h5>
                    </React.Fragment>
                  ) : (
                    <h4 className="video-title font-weight-bold">
                      Live in&nbsp;
                      <span className="pretty_time">
                        {playlist.scheduled.pretty_countdown}
                      </span>
                    </h4>
                  )}
                </div>
                {(playlist.video?.duration || playlist.video?.timestamp) && (
                  <div className="d-flex justify-content-start">
                    <div className="video-duration text-secondary-color description mb-0">
                      {playlist.video.duration}
                      {playlist.video.duration && playlist.video.timestamp && (
                        <React.Fragment> &bull;&nbsp;</React.Fragment>
                      )}
                    </div>
                    <div className="video-created text-secondary-color description mb-0 posted">
                      {getFormatUTCTime(
                        playlist.video.timestamp,
                        'MMM. D, YYYY'
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col lg={4} className="p-0 d-flex flex-column">
            {videosJsx.length > 0 ? (
              <div className="flex-grow-1 playlist-container d-flex flex-row flex-lg-column">
                {videosJsx}
              </div>
            ) : (
              <h3>No upcoming videos.</h3>
            )}
          </Col>
        </Row>
      </div>
    </Col>
  );
}
